import React from 'react';
import { ArrowRight, ShieldCheck, CheckCircle } from 'lucide-react';
import { Badge } from '../ui';
import { HeroTechnookVisual } from './HeroTechnookVisual';

export interface HeroSectionProps {
  onOpenConsultation?: () => void;
  onExploreServices?: () => void;
}

export const HeroSection: React.FC<HeroSectionProps> = ({ onOpenConsultation, onExploreServices }) => {
  return (
    <section id="home" className="relative pt-24 pb-20 sm:pt-28 sm:pb-24 lg:pt-32 lg:pb-28 min-h-[780px] flex items-center overflow-hidden bg-gradient-to-br from-[#4a82c2] via-[#3b75b4] to-[#184166] dark:from-[#080e1a] dark:via-[#0d172a] dark:to-[#080e1a] text-white">
      {/* Background ambient decorative shapes */}
      <div className="absolute top-0 right-0 w-1/3 h-full bg-white/5 dark:bg-sky-500/5 clip-path-polygon pointer-events-none" />
      <div className="absolute bottom-10 left-10 w-96 h-96 bg-[#08adae]/20 rounded-full blur-3xl pointer-events-none" />

      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 w-full relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
          {/* Left Column: Technook Core Headline & Content */}
          <div className="lg:col-span-7 space-y-4 sm:space-y-5 text-left">
            <div>
              <Badge variant="brand" className="bg-white/10 text-white border-white/20 px-3.5 py-1 text-[11px] sm:text-xs uppercase tracking-wider">
                SOFTWARE AND SYSTEM DEVELOPMENT
              </Badge>
            </div>

            {/* Giant Decorative Technook Quote Mark */}
            <div className="text-4xl sm:text-5xl lg:text-6xl font-serif text-white/30 dark:text-[#08adae]/40 font-black leading-none -mb-3 sm:-mb-4 select-none">
              “
            </div>

            <h1 className="text-3xl sm:text-5xl lg:text-[3.5rem] font-extrabold text-white tracking-tight leading-[1.12]">
              A dedicated space where{' '}
              <span className="text-white dark:text-[#08adae] underline decoration-[#08adae]/40 decoration-4">
                technology
              </span>{' '}
              and{' '}
              <span className="text-white dark:text-sky-300">
                innovation
              </span>{' '}
              come together.
            </h1>

            <p className="text-sm sm:text-base lg:text-lg text-white/90 dark:text-slate-300 max-w-2xl leading-relaxed font-normal pt-1">
              A trusted technology partner for businesses ready to build smarter, operate better, and grow with confidence across web platforms, mobile apps, marketing assets, and lead generation pipelines.
            </p>

            {/* Key Micro Highlights */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5 pt-1 sm:pt-2">
              <div className="flex items-center gap-2 text-xs sm:text-sm font-semibold text-white/90 dark:text-slate-300">
                <CheckCircle size={15} className="text-[#08adae] shrink-0" />
                <span>Web & Mobile Systems</span>
              </div>
              <div className="flex items-center gap-2 text-xs sm:text-sm font-semibold text-white/90 dark:text-slate-300">
                <CheckCircle size={15} className="text-[#08adae] shrink-0" />
                <span>ROI-First Engineering</span>
              </div>
              <div className="flex items-center gap-2 text-xs sm:text-sm font-semibold text-white/90 dark:text-slate-300">
                <CheckCircle size={15} className="text-[#08adae] shrink-0" />
                <span>Dedicated Operations</span>
              </div>
            </div>

            {/* Technook Action Buttons */}
            <div className="flex flex-wrap items-center gap-3 sm:gap-4 pt-2 sm:pt-3">
              <button
                onClick={onOpenConsultation}
                className="px-5 py-3 sm:px-6 sm:py-3.5 rounded-xl bg-white hover:bg-slate-100 text-[#184166] dark:text-slate-900 font-bold text-xs sm:text-sm shadow-xl hover:shadow-2xl transition-all duration-200 flex items-center gap-2 active:scale-95"
              >
                <span>Discuss your project</span>
              </button>
              <button
                onClick={onExploreServices}
                className="px-3.5 py-3 sm:px-4 sm:py-3.5 text-xs sm:text-sm font-semibold text-white hover:text-sky-200 dark:hover:text-[#08adae] flex items-center gap-2 transition-colors group"
              >
                <span>Explore our services</span>
                <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform" />
              </button>
            </div>

            {/* Trust Footer */}
            <div className="pt-4 sm:pt-5 border-t border-white/20 dark:border-slate-800 flex items-center gap-4 sm:gap-6 text-xs text-white/80 dark:text-slate-400 font-medium">
              <span className="flex items-center gap-1.5">
                <ShieldCheck size={15} className="text-emerald-400" /> Scalable Enterprise Codebase
              </span>
              <span>•</span>
              <span>Imperial Plaza, Parañaque City</span>
            </div>
          </div>

          {/* Right Column: HeroTechnookVisual Emblem Presentation */}
          <div className="lg:col-span-5 w-full">
            <HeroTechnookVisual />
          </div>
        </div>
      </div>
    </section>
  );
};
